import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { feedService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { Card, Button } from './ui';
import { isImageAttachment, attachmentFileName } from '../utils/attachments';
import { linkifyText, extractUrls } from '../utils/linkify';

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

function timeAgo(iso, t) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('Just now');
  if (mins < 60) return t('{{count}}m ago', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('{{count}}h ago', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('{{count}}d ago', { count: days });
  return new Date(iso).toLocaleDateString();
}

function PostComments({ post, currentUser, canModerate, onCommentAdded }) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [comments, setComments] = useState(null);
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    feedService.getComments(post.id)
      .then((res) => setComments(res.data || []))
      .catch(() => setComments([]));
  }, [post.id]);

  const submit = async () => {
    const content = input.trim();
    if (!content) return;
    setSubmitting(true);
    try {
      const res = await feedService.addComment(post.id, content);
      setComments((prev) => [...(prev || []), res.data]);
      setInput('');
      onCommentAdded(post.id);
    } catch (err) {
      addNotification(t('Failed to post comment'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (commentId) => {
    try {
      await feedService.deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      addNotification(t('Failed to delete comment'), 'error');
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
      {comments === null ? (
        <div className="text-sm text-gray-400">{t('Loading...')}</div>
      ) : (
        comments.map((c) => (
          <div key={c.id} className="flex items-start gap-2 text-sm">
            <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-xs shrink-0">
              {(c.author.first_name || '?')[0]}
            </div>
            <div className="flex-1 min-w-0 bg-gray-50 rounded-lg px-3 py-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-800">{c.author.first_name} {c.author.last_name}</span>
                {(c.author.id === currentUser?.id || canModerate) && (
                  <button onClick={() => remove(c.id)} className="text-xs text-gray-400 hover:text-red-500">{t('Delete')}</button>
                )}
              </div>
              <p className="text-gray-700 break-words">{linkifyText(c.content)}</p>
            </div>
          </div>
        ))
      )}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder={t('Write a comment...')}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-300"
        />
        <button
          onClick={submit}
          disabled={!input.trim() || submitting}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          {t('Send')}
        </button>
      </div>
    </div>
  );
}

export default function CourseFeed({ moduleId, isTeacher, isAdmin, currentUser }) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const confirm = useConfirm();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const fileInputRef = useRef(null);

  const canModerate = isTeacher || isAdmin;

  const fetchPosts = useCallback(() => {
    setLoading(true);
    feedService.getPosts({ module: moduleId })
      .then((res) => {
        const data = res.data;
        setPosts(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [moduleId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleAttachmentPick = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_SIZE) {
      addNotification(t('Attachment must be smaller than 25MB'), 'error');
      return;
    }
    setAttachment(file);
    setAttachmentPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
  };

  const clearAttachment = () => {
    if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    setAttachment(null);
    setAttachmentPreview(null);
  };

  const submitPost = async () => {
    if (!content.trim() && !attachment) return;
    setPosting(true);
    try {
      const res = await feedService.createPost({ module: moduleId, content: content.trim(), attachment });
      setPosts((prev) => [res.data, ...prev]);
      setContent('');
      clearAttachment();
    } catch (err) {
      addNotification(t('Failed to publish post'), 'error');
    } finally {
      setPosting(false);
    }
  };

  const removePost = async (postId) => {
    if (!(await confirm(t('Delete this post?')))) return;
    try {
      await feedService.deletePost(postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      addNotification(t('Failed to delete post'), 'error');
    }
  };

  const toggleReaction = async (post) => {
    setPosts((prev) => prev.map((p) => (
      p.id === post.id
        ? { ...p, my_reaction: !p.my_reaction, reactions_count: p.reactions_count + (p.my_reaction ? -1 : 1) }
        : p
    )));
    try {
      await feedService.reactToPost(post.id);
    } catch (err) {
      // roll back on failure
      setPosts((prev) => prev.map((p) => (p.id === post.id ? post : p)));
    }
  };

  const bumpCommentCount = (postId) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p)));
  };

  return (
    <div className="space-y-4">
      {isTeacher && (
        <Card>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('Share an update with your class...')}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-300"
            rows={3}
          />
          {attachmentPreview && (
            <div className="mt-2 relative inline-block">
              <img src={attachmentPreview} alt="" className="h-24 rounded-lg border" />
              <button onClick={clearAttachment} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs">✕</button>
            </div>
          )}
          {attachment && !attachmentPreview && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 w-fit">
              <span>📎 {attachment.name}</span>
              <button onClick={clearAttachment} className="text-gray-400 hover:text-red-500">✕</button>
            </div>
          )}
          <div className="flex items-center justify-between mt-3">
            <input type="file" ref={fileInputRef} onChange={handleAttachmentPick} className="hidden" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-400 hover:text-gray-600"
              title={t('Attach a file')}
              aria-label={t('Attach a file')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <Button onClick={submitPost} loading={posting} disabled={!content.trim() && !attachment}>
              {t('Post')}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
      ) : posts.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-gray-500">{t('No posts yet')}</div>
        </Card>
      ) : (
        posts.map((post) => (
          <Card key={post.id}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-semibold shrink-0">
                  {(post.author.first_name || '?')[0]}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{post.author.first_name} {post.author.last_name}</p>
                  <p className="text-xs text-gray-500">{timeAgo(post.created_at, t)}</p>
                </div>
              </div>
              {(post.author.id === currentUser?.id || canModerate) && (
                <button onClick={() => removePost(post.id)} className="text-xs text-gray-400 hover:text-red-500 shrink-0">
                  {t('Delete')}
                </button>
              )}
            </div>

            {post.content && (
              <p className="mt-3 text-gray-800 whitespace-pre-line break-words">{linkifyText(post.content)}</p>
            )}

            {/* Facebook-style: a pasted link that points straight at an image renders as
                an inline preview, same as an uploaded attachment would. */}
            {extractUrls(post.content).filter(isImageAttachment).map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block mt-3">
                <img src={url} alt="" className="max-h-96 w-auto rounded-lg border border-gray-100" />
              </a>
            ))}

            {post.attachment && (
              isImageAttachment(post.attachment) ? (
                <a href={post.attachment} target="_blank" rel="noopener noreferrer" className="block mt-3">
                  <img src={post.attachment} alt="" className="max-h-96 w-auto rounded-lg border border-gray-100" />
                </a>
              ) : (
                <a
                  href={post.attachment}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center gap-2 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 w-fit"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {attachmentFileName(post.attachment) || t('Attachment')}
                </a>
              )
            )}

            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => toggleReaction(post)}
                className={`flex items-center gap-1.5 text-sm font-medium transition ${post.my_reaction ? 'text-primary-600' : 'text-gray-500 hover:text-primary-600'}`}
              >
                <svg className="w-5 h-5" fill={post.my_reaction ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2" />
                </svg>
                {post.reactions_count > 0 ? post.reactions_count : t('Like')}
              </button>
              <button
                onClick={() => setOpenComments((prev) => ({ ...prev, [post.id]: !prev[post.id] }))}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-primary-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                {post.comments_count > 0
                  ? (post.comments_count === 1 ? t('{{count}} comment', { count: 1 }) : t('{{count}} comments', { count: post.comments_count }))
                  : t('Comment')}
              </button>
            </div>

            {openComments[post.id] && (
              <PostComments post={post} currentUser={currentUser} canModerate={canModerate} onCommentAdded={bumpCommentCount} />
            )}
          </Card>
        ))
      )}
    </div>
  );
}
